/**
 * Button: the one way to trigger an action (cockatrice-ui `src/ui/Button.jsx`, ported to hyperscript; PLAN.md §0).
 * Variants: primary (one per view, ink blue), secondary, ghost, danger, link.
 * Renders an <a> when href is given, so navigation stays a real link.
 */
import './css/button.css';
import { mergeProps, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import { component, el, spread, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

export interface ButtonProps extends Attributes {
  /** primary (one per view), secondary, ghost, danger (says what it destroys), link. */
  readonly variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'link' | undefined;
  /** 36 / 40 / 46 px tall (each at least the 44px target, decision 71). Ignored by the link variant. */
  readonly size?: 'sm' | 'md' | 'lg' | undefined;
  /** Icon before the label. */
  readonly iconStart?: IconName | undefined;
  /** Icon after the label (chevronDown for menus, external for links that leave the app). */
  readonly iconEnd?: IconName | undefined;
  /** Shows a spinner, keeps the width, sets aria-busy and ignores clicks. */
  readonly loading?: boolean | undefined;
  /** Faded and not clickable. On a link, the href is removed. */
  readonly disabled?: boolean | undefined;
  /** Fills the container; long labels wrap. */
  readonly fullWidth?: boolean | undefined;
  /** Renders an <a> instead of a <button>. */
  readonly href?: string | undefined;
  /** Button type. Defaults to "button"; use "submit" in forms. */
  readonly type?: 'button' | 'submit' | 'reset' | undefined;
  /** Extra class on the button. */
  readonly class?: string | undefined;
  /** The label. */
  readonly children?: Child;
  /** Runs on a click, unless the button is disabled or loading. */
  readonly onClick?: ((event: MouseEvent) => void) | undefined;
  /**
   * Said when the button isn't loading: a submit that is off but still focusable and pressable, so its form can say why
   * (the handoff's TwoFactor sheet's `aria-disabled` Continue). Loading always says "true".
   */
  readonly 'aria-disabled'?: 'true' | 'false' | undefined;
  /** Said when the button isn't loading (work under way that keeps the label in view). Loading always says "true". */
  readonly 'aria-busy'?: 'true' | 'false' | undefined;
}

export default function Button(props: ButtonProps): Child {
  const p = mergeProps({ variant: 'secondary', size: 'md', type: 'button' } as const, props);
  const [local, rest] = splitProps(p, ['variant', 'size', 'iconStart', 'iconEnd', 'loading', 'fullWidth', 'class', 'children', 'href', 'type', 'disabled', 'onClick', 'aria-disabled', 'aria-busy']);

  const blocked = (): boolean => local.disabled === true || local.loading === true;
  // The caller's own state, said when the button has none of its own to say.
  const busy = (): 'true' | 'false' | undefined => (local.loading === true ? 'true' : local['aria-busy']);
  const iconSize = (): 'sm' | 'md' => (local.size === 'lg' ? 'md' : 'sm');

  const cls = (): string =>
    [
      'ui-button',
      `ui-button--${local.variant}`,
      local.variant !== 'link' && `ui-button--${local.size}`,
      local.fullWidth === true && 'ui-button--full',
      local.loading === true && 'is-loading',
      local.class,
    ]
      .filter(Boolean)
      .join(' ');

  const onClick = (event: MouseEvent): void => {
    if (blocked()) {
      event.preventDefault();
      return;
    }
    local.onClick?.(event);
  };

  const inner = (): Child => [
    el(
      'span',
      { class: 'ui-button__content' },
      when(
        () => local.iconStart !== undefined,
        () => component(Icon, { get name() { return local.iconStart ?? 'plus'; }, get size() { return iconSize(); }, class: 'ui-button__icon' }),
      ),
      el('span', { class: 'ui-button__label' }, () => local.children),
      when(
        () => local.iconEnd !== undefined,
        () => component(Icon, { get name() { return local.iconEnd ?? 'plus'; }, get size() { return iconSize(); }, class: 'ui-button__icon' }),
      ),
    ),
    when(
      () => local.loading === true,
      () => el('span', { class: 'ui-button__spinner', 'aria-hidden': 'true' }),
    ),
  ];

  return when(
    () => local.href !== undefined && local.href !== '',
    () =>
      el(
        'a',
        spread(rest, {
          href: () => (local.disabled === true ? undefined : local.href),
          class: cls,
          'aria-disabled': () => (blocked() ? 'true' : local['aria-disabled']),
          'aria-busy': busy,
          onClick,
        }),
        inner(),
      ),
    () =>
      el(
        'button',
        spread(rest, {
          type: () => local.type,
          class: cls,
          disabled: () => local.disabled === true,
          'aria-disabled': () => (local.loading === true ? 'true' : local['aria-disabled']),
          'aria-busy': busy,
          onClick,
        }),
        inner(),
      ),
  );
}
